import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GlobalStyles } from './styles/GlobalStyles';
import { pageview } from './utils/gtag';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppWidget from './components/WhatsAppWidget';

// Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import DrivewaysPage from './pages/DrivewaysPage';
import PathwaysPage from './pages/PathwaysPage';
import PoolDecksPage from './pages/PoolDecksPage';
import WhatIsResinBoundPage from './pages/WhatIsResinBoundPage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import AreasPage from './pages/AreasPage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import FAQPage from './pages/FAQPage';

// Blog Articles
import UltimateGuideResinBoundPage from './pages/blog/UltimateGuideResinBoundPage';
import ResinDrivewayCostGuidePage from './pages/blog/ResinDrivewayCostGuidePage';
import ResinPathwayDesignIdeasPage from './pages/blog/ResinPathwayDesignIdeasPage';
import ResinSurfacingMaintenanceTipsPage from './pages/blog/ResinSurfacingMaintenanceTipsPage';
import PoolDeckSurfacingBenefitsPage from './pages/blog/PoolDeckSurfacingBenefitsPage';
import ResinVsConcreteComparisonPage from './pages/blog/ResinVsConcreteComparisonPage';
import ResinBoundInstallationProcessPage from './pages/blog/ResinBoundInstallationProcessPage';
import SeasonalCareWeatherConsiderationsPage from './pages/blog/SeasonalCareWeatherConsiderationsPage';
import DIYvsProfessionalInstallationPage from './pages/blog/DIYvsProfessionalInstallationPage';
import ResinBoundTroubleshootingGuidePage from './pages/blog/ResinBoundTroubleshootingGuidePage';
import BoroughLondonGuidePage from './pages/blog/BoroughLondonGuidePage';
import SeoArticlePage from './pages/blog/SeoArticlePage';
import WestminsterPage from './pages/WestminsterPage';
import CamdenPage from './pages/CamdenPage';
import IslingtonPage from './pages/IslingtonPage';
import KensingtonPage from './pages/KensingtonPage';
import HackneyPage from './pages/HackneyPage';
import TowerHamletsPage from './pages/TowerHamletsPage';
import GreenwichPage from './pages/GreenwichPage';
import SouthwarkPage from './pages/SouthwarkPage';
import LambethPage from './pages/LambethPage';
import WandsworthPage from './pages/WandsworthPage';
import HammersmithPage from './pages/HammersmithPage';
import ChelseaPage from './pages/ChelseaPage';
import FulhamPage from './pages/FulhamPage';
import RichmondPage from './pages/RichmondPage';
import KingstonPage from './pages/KingstonPage';
import MertonPage from './pages/MertonPage';
import SuttonPage from './pages/SuttonPage';
import CroydonPage from './pages/CroydonPage';
import BromleyPage from './pages/BromleyPage';
import BexleyPage from './pages/BexleyPage';
import HaveringPage from './pages/HaveringPage';
import RedbridgePage from './pages/RedbridgePage';
import WalthamForestPage from './pages/WalthamForestPage';
import EnfieldPage from './pages/EnfieldPage';
import BarnetPage from './pages/BarnetPage';
import HaringeyPage from './pages/HaringeyPage';
import NewhamPage from './pages/NewhamPage';
import BarkingPage from './pages/BarkingPage';
import HounslowPage from './pages/HounslowPage';
import EalingPage from './pages/EalingPage';
import BrentPage from './pages/BrentPage';
import HarrowPage from './pages/HarrowPage';
import HillingdonPage from './pages/HillingdonPage';
import SurreyPage from './pages/SurreyPage';
import KentPage from './pages/KentPage';
import EssexPage from './pages/EssexPage';
import HertfordshirePage from './pages/HertfordshirePage';
import BuckinghamshirePage from './pages/BuckinghamshirePage';
import BerkshirePage from './pages/BerkshirePage';
import SitemapPage from './pages/SitemapPage';

// Component to track page views
const GoogleAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    pageview(location.pathname + location.search);
  }, [location]);

  return null;
};


function App() {
  return (
    <HelmetProvider>
      <GlobalStyles />
      <Router>
        <GoogleAnalytics />
        <ScrollToTop />
        <div className="App">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/resin-driveways-london/" element={<DrivewaysPage />} />
              <Route path="/resin-pathways-london/" element={<PathwaysPage />} />
              <Route path="/resin-patios-london/" element={<PathwaysPage />} />
              <Route path="/resin-paving-london/" element={<PathwaysPage />} />
              <Route path="/resin-pool-decks-london/" element={<PoolDecksPage />} />
              <Route path="/what-is-resin-bound-surfacing/" element={<WhatIsResinBoundPage />} />
              <Route path="/about-us/" element={<AboutPage />} />
              <Route path="/gallery/" element={<GalleryPage />} />
              <Route path="/areas-we-cover/" element={<AreasPage />} />
              <Route path="/blog/" element={<BlogPage />} />
              <Route path="/contact/" element={<ContactPage />} />
              <Route path="/terms-conditions/" element={<TermsConditionsPage />} />
              <Route path="/privacy-policy/" element={<PrivacyPolicyPage />} />
              <Route path="/cookies/" element={<CookiePolicyPage />} />
              <Route path="/faq/" element={<FAQPage />} />
              
              {/* Blog Articles */}
              <Route path="/blog/ultimate-guide-resin-bound-surfacing/" element={<UltimateGuideResinBoundPage />} />
              <Route path="/blog/resin-driveway-cost-guide/" element={<ResinDrivewayCostGuidePage />} />
              <Route path="/blog/resin-pathway-design-ideas/" element={<ResinPathwayDesignIdeasPage />} />
              <Route path="/blog/resin-surfacing-maintenance-tips/" element={<ResinSurfacingMaintenanceTipsPage />} />
              <Route path="/blog/pool-deck-surfacing-benefits/" element={<PoolDeckSurfacingBenefitsPage />} />
              <Route path="/blog/resin-vs-concrete-comparison/" element={<ResinVsConcreteComparisonPage />} />
              <Route path="/blog/resin-bound-installation-process/" element={<ResinBoundInstallationProcessPage />} />
              <Route path="/blog/seasonal-care-weather-considerations/" element={<SeasonalCareWeatherConsiderationsPage />} />
              <Route path="/blog/diy-vs-professional-installation/" element={<DIYvsProfessionalInstallationPage />} />
              <Route path="/blog/resin-bound-troubleshooting-guide/" element={<ResinBoundTroubleshootingGuidePage />} />
              <Route path="/blog/guides/:slug/" element={<SeoArticlePage mode="topic" />} />
              <Route path="/blog/regions/:slug/" element={<SeoArticlePage mode="region" />} />
              <Route path="/blog/london/:routeSlug/" element={<BoroughLondonGuidePage />} />
              
              {/* Borough Pages */}
              <Route path="/resin-driveways-westminster/" element={<WestminsterPage />} />
              <Route path="/resin-driveways-camden/" element={<CamdenPage />} />
              <Route path="/resin-driveways-islington/" element={<IslingtonPage />} />
              <Route path="/resin-driveways-kensington/" element={<KensingtonPage />} />
              <Route path="/resin-driveways-hackney/" element={<HackneyPage />} />
              <Route path="/resin-driveways-tower-hamlets/" element={<TowerHamletsPage />} />
              <Route path="/resin-driveways-greenwich/" element={<GreenwichPage />} />
              <Route path="/resin-driveways-southwark/" element={<SouthwarkPage />} />
              <Route path="/resin-driveways-lambeth/" element={<LambethPage />} />
              <Route path="/resin-driveways-wandsworth/" element={<WandsworthPage />} />
              <Route path="/resin-driveways-hammersmith/" element={<HammersmithPage />} />
              <Route path="/resin-driveways-chelsea/" element={<ChelseaPage />} />
              <Route path="/resin-driveways-fulham/" element={<FulhamPage />} />
              <Route path="/resin-driveways-richmond/" element={<RichmondPage />} />
              <Route path="/resin-driveways-kingston/" element={<KingstonPage />} />
              <Route path="/resin-driveways-merton/" element={<MertonPage />} />
              <Route path="/resin-driveways-sutton/" element={<SuttonPage />} />
              <Route path="/resin-driveways-croydon/" element={<CroydonPage />} />
              <Route path="/resin-driveways-bromley/" element={<BromleyPage />} />
              <Route path="/resin-driveways-bexley/" element={<BexleyPage />} />
              <Route path="/resin-driveways-havering/" element={<HaveringPage />} />
              <Route path="/resin-driveways-redbridge/" element={<RedbridgePage />} />
              <Route path="/resin-driveways-waltham-forest/" element={<WalthamForestPage />} />
              <Route path="/resin-driveways-enfield/" element={<EnfieldPage />} />
              <Route path="/resin-driveways-barnet/" element={<BarnetPage />} />
              <Route path="/resin-driveways-haringey/" element={<HaringeyPage />} />
              <Route path="/resin-driveways-newham/" element={<NewhamPage />} />
              <Route path="/resin-driveways-barking/" element={<BarkingPage />} />
              <Route path="/resin-driveways-hounslow/" element={<HounslowPage />} />
              <Route path="/resin-driveways-ealing/" element={<EalingPage />} />
              <Route path="/resin-driveways-brent/" element={<BrentPage />} />
              <Route path="/resin-driveways-harrow/" element={<HarrowPage />} />
              <Route path="/resin-driveways-hillingdon/" element={<HillingdonPage />} />
              
              {/* County Pages */}
              <Route path="/resin-driveways-surrey/" element={<SurreyPage />} />
              <Route path="/resin-driveways-kent/" element={<KentPage />} />
              <Route path="/resin-driveways-essex/" element={<EssexPage />} />
              <Route path="/resin-driveways-hertfordshire/" element={<HertfordshirePage />} />
              <Route path="/resin-driveways-buckinghamshire/" element={<BuckinghamshirePage />} />
              <Route path="/resin-driveways-berkshire/" element={<BerkshirePage />} />
              
              {/* Sitemap Routes - Serve XML content without header/footer */}
              <Route path="/sitemap.xml" element={
                <div className="App">
                  <main>
                    <SitemapPage />
                  </main>
                </div>
              } />
              <Route path="/sitemap-index.xml" element={
                <div className="App">
                  <main>
                    <SitemapPage />
                  </main>
                </div>
              } />
              <Route path="/sitemap-images.xml" element={
                <div className="App">
                  <main>
                    <SitemapPage />
                  </main>
                </div>
              } />
              <Route path="/robots.txt" element={
                <div className="App">
                  <main>
                    <SitemapPage />
                  </main>
                </div>
              } />
            </Routes>
          </main>
          <Footer />
          <WhatsAppWidget />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
